/**
 * Decide qual organização fica selecionada depois de buscar a lista de orgs do usuário.
 *
 * A regra que importa: **falha na busca não é lista vazia.** Antes, a busca engolia qualquer erro
 * (rede, requisição abortada por recarga da página, JWT no meio do refresh, 5xx, falha na página 2
 * da paginação) e devolvia `[]`. Quem escolhia a org lia `[]` como "o usuário não tem org",
 * apagava `current_org_id` do localStorage, e a carga seguinte caía na primeira org por id. Para
 * quem participa de muitas orgs isso é uma org arbitrária, sem aviso (caso de 22/09/2026 em dev:
 * dezenas de recargas do Vite num merge trocaram a Midhaus por uma "Minha Organização" e o
 * módulo de Operações sumiu do menu).
 *
 * Função pura para poder ser testada sem montar o provider.
 */

export type ResultadoBuscaOrgs<T extends { id: string }> =
  | { ok: true; orgs: T[] }
  | { ok: false; erro: string };

export type DecisaoOrgAtual<T extends { id: string }> =
  /** A busca falhou: não mexer no estado nem na org guardada. */
  | { tipo: 'manter'; erro: string }
  /** A busca funcionou e o usuário não tem nenhuma org ativa. */
  | { tipo: 'sem-org' }
  /**
   * Selecionar `org`. `descartouGuardada` indica que havia uma org guardada que não está mais
   * na lista (membership removida, org desativada): só nesse caso a escolha anterior se perde.
   */
  | { tipo: 'selecionar'; org: T; descartouGuardada: boolean; daAba?: true };

/**
 * `orgDaAbaId` é a org escolhida só para esta aba (`orgDaAba.ts`). Vence a guardada quando está
 * na lista e vem com `daAba: true`, para quem chama não gravá-la no localStorage. Fora da lista,
 * é ignorada e a resolução segue a guardada.
 */
export function resolverOrgAtual<T extends { id: string }>(
  orgGuardadaId: string | null,
  resultado: ResultadoBuscaOrgs<T>,
  orgDaAbaId: string | null = null,
): DecisaoOrgAtual<T> {
  if (resultado.ok === false) return { tipo: 'manter', erro: resultado.erro };
  if (resultado.orgs.length === 0) return { tipo: 'sem-org' };

  const daAba = orgDaAbaId ? resultado.orgs.find((o) => o.id === orgDaAbaId) : undefined;
  if (daAba) return { tipo: 'selecionar', org: daAba, descartouGuardada: false, daAba: true };

  const guardada = orgGuardadaId ? resultado.orgs.find((o) => o.id === orgGuardadaId) : undefined;
  if (guardada) return { tipo: 'selecionar', org: guardada, descartouGuardada: false };

  return { tipo: 'selecionar', org: resultado.orgs[0], descartouGuardada: orgGuardadaId !== null };
}
